import React, { useState, useRef } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { TrendingUpIcon, MegaphoneIcon, MonitorIcon, DollarSignIcon, UsersIcon, HeadphonesIcon, CrownIcon, BuildingIcon, ShieldIcon, GlobeIcon } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useTranslations } from '../i18n/translations';
const nicheIcons = [<CrownIcon className="w-5 h-5" data-id="element-349" />, <MonitorIcon className="w-5 h-5" data-id="element-350" />, <DollarSignIcon className="w-5 h-5" data-id="element-351" />, <TrendingUpIcon className="w-5 h-5" data-id="element-352" />, <MegaphoneIcon className="w-5 h-5" data-id="element-353" />, <UsersIcon className="w-5 h-5" data-id="element-354" />, <BuildingIcon className="w-5 h-5" data-id="element-355" />, <ShieldIcon className="w-5 h-5" data-id="element-356" />, <GlobeIcon className="w-5 h-5" data-id="element-357" />, <HeadphonesIcon className="w-5 h-5" data-id="element-358" />];
const nicheStyles = [{
  color: 'text-navy-700',
  bgColor: 'bg-navy-100',
  borderColor: 'border-navy-300'
}, {
  color: 'text-violet-600',
  bgColor: 'bg-violet-100',
  borderColor: 'border-violet-300'
}, {
  color: 'text-emerald-600',
  bgColor: 'bg-emerald-100',
  borderColor: 'border-emerald-300'
}, {
  color: 'text-blue-600',
  bgColor: 'bg-blue-100',
  borderColor: 'border-blue-300'
}, {
  color: 'text-pink-600',
  bgColor: 'bg-pink-100',
  borderColor: 'border-pink-300'
}, {
  color: 'text-primary-600',
  bgColor: 'bg-primary-100',
  borderColor: 'border-primary-300'
}, {
  color: 'text-orange-600',
  bgColor: 'bg-orange-100',
  borderColor: 'border-orange-300'
}, {
  color: 'text-amber-600',
  bgColor: 'bg-amber-100',
  borderColor: 'border-amber-300'
}, {
  color: 'text-rose-600',
  bgColor: 'bg-rose-100',
  borderColor: 'border-rose-300'
}, {
  color: 'text-teal-600',
  bgColor: 'bg-teal-100',
  borderColor: 'border-teal-300'
}];
// Positions stay as-is (mostly international role names)
const nichePositions = [['CEO / Генеральний директор', 'COO / Операційний директор', 'CFO / Фінансовий директор', 'CTO / Технічний директор', 'CMO / Директор з маркетингу', 'CHRO / Директор з персоналу', 'CPO / Директор з продукту', 'CSO / Директор зі стратегії'], ['CTO / VP of Engineering', 'Tech Lead / Team Lead', 'Frontend Developer (React, Angular, Vue)', 'Backend Developer (Node, Go, Java, Python)', 'Full‑stack Developer', 'DevOps / SRE Engineer', 'QA Engineer / QA Lead', 'Product Manager / Product Owner', 'UI/UX Designer', 'Data Engineer / Data Scientist', 'Mobile Developer (iOS, Android)', 'Solutions Architect', 'Scrum Master / Agile Coach', 'IT Project Manager', 'Cybersecurity Engineer', 'Cloud Engineer (AWS, Azure, GCP)'], ['CFO / Financial Director', 'Financial Analyst', 'Chief Accountant', 'Auditor / Internal Auditor', 'Financial Controller', 'Risk Manager', 'Compliance Officer', 'Treasury Manager'], ['Commercial Director', 'Head of Sales', 'Key Account Manager', 'Business Development Manager', 'Sales Director', 'Sales Representative', 'Head of Partnerships', 'Regional Sales Manager'], ['CMO / Marketing Director', 'Digital Marketing Manager', 'Brand Manager', 'Performance Marketing Manager', 'PR Director', 'Content Marketing Manager', 'Growth Manager', 'Head of Communications'], ['CHRO / HR Director', 'HR Business Partner', 'Head of Recruitment', 'C&B Manager', 'HR Analyst', 'L&D Manager', 'Employer Brand Manager', 'People Operations Manager'], ['COO / Operations Director', 'Supply Chain Director', 'Logistics Manager', 'Procurement Director', 'Warehouse Manager', 'Operations Manager', 'Procurement Manager', 'Fleet Manager'], ['General Counsel / Legal Director', 'Corporate Lawyer', 'Compliance Manager', 'Labor Law Attorney', 'IP Lawyer', 'Litigation Lawyer', 'Legal Counsel', 'Data Privacy Officer (DPO)'], ['E‑commerce Director', 'Category Manager', 'Merchandiser', 'Retail Operations Manager', 'Marketplace Manager', 'Head of Online Sales', 'CRM Manager', 'Trade Marketing Manager'], ['Personal Assistant to CEO', 'Office Manager', 'Executive Assistant', 'Project Coordinator', 'Customer Success Manager', 'Head of Administration', 'Facilities Manager', 'Business Analyst']];
export function NichesSection() {
  const [activeIndex, setActiveIndex] = useState(0);
  const {
    lang
  } = useLanguage();
  const t = useTranslations(lang);
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  const niches = t.niches.categories.map((cat, i) => ({
    title: cat.title,
    icon: nicheIcons[i],
    positions: nichePositions[i],
    ...nicheStyles[i]
  }));
  const active = niches[activeIndex];
  return <section className="py-20 lg:py-28 bg-navy-50/50 overflow-hidden" id="niches" data-id="element-359">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" ref={ref} data-id="element-360">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5
      }} className="text-center mb-14" data-id="element-361">
          <span className="text-sm font-semibold text-primary-600 tracking-wide uppercase" data-id="element-362">
            {t.niches.eyebrow}
          </span>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-navy-900 tracking-tight" data-id="element-363">
            {t.niches.title}
          </h2>
          <p className="mt-4 text-lg text-navy-500 max-w-2xl mx-auto" data-id="element-364">
            {t.niches.subtitle}
          </p>
          <p className="mt-3 text-sm text-navy-400 max-w-xl mx-auto" data-id="element-365">
            {t.niches.note}
          </p>
        </motion.div>

        {/* Mobile: horizontal scroll tabs */}
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5,
        delay: 0.1
      }} className="lg:hidden mb-6 -mx-4 px-4" data-id="element-366">
          <div className="flex gap-2 overflow-x-auto pb-3 scrollbar-hide" data-id="element-367">
            {niches.map((niche, index) => <button key={niche.title} onClick={() => setActiveIndex(index)} className={`flex-shrink-0 flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all whitespace-nowrap ${activeIndex === index ? `${niche.bgColor} ${niche.color} shadow-sm` : 'bg-white text-navy-500 border border-navy-200'}`} data-id="element-368">
                {niche.icon}
                {niche.title}
              </button>)}
          </div>
        </motion.div>

        {/* Mobile: positions panel */}
        <div className="lg:hidden" data-id="element-369">
          <AnimatePresence mode="wait" data-id="element-370">
            <motion.div key={activeIndex} initial={{
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -8
          }} transition={{
            duration: 0.2
          }} className="bg-white rounded-2xl border border-navy-100 p-6" data-id="element-371">
              <div className="flex items-center gap-3 mb-5" data-id="element-372">
                <div className={`w-10 h-10 rounded-xl ${active.bgColor} ${active.color} flex items-center justify-center`} data-id="element-373">
                  {active.icon}
                </div>
                <div data-id="element-374">
                  <h3 className="font-bold text-navy-900" data-id="element-375">{active.title}</h3>
                  <p className="text-xs text-navy-400" data-id="element-376">
                    {active.positions.length} {t.niches.positions_label}
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-id="element-377">
                {active.positions.map(pos => <div key={pos} className="flex items-center gap-2.5 py-2 px-3 rounded-lg bg-navy-50/50 text-sm text-navy-600" data-id="element-378">
                    <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${active.bgColor}`} data-id="element-379" />
                    {pos}
                  </div>)}
                <div className="flex items-center gap-2.5 py-2 px-3 rounded-lg border border-dashed border-navy-200 text-sm text-navy-400 italic" data-id="element-380">
                  <span className="w-1.5 h-1.5 rounded-full flex-shrink-0 bg-navy-200" data-id="element-381" />
                  {t.niches.other_positions}
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Desktop: side-by-side tabs + panel */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5,
        delay: 0.15
      }} className="hidden lg:grid lg:grid-cols-12 gap-6" data-id="element-382">
          <div className="col-span-4 space-y-1.5" data-id="element-383">
            {niches.map((niche, index) => <button key={niche.title} onClick={() => setActiveIndex(index)} className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-xl text-left transition-all duration-200 group ${activeIndex === index ? `bg-white shadow-md border ${niche.borderColor}` : 'hover:bg-white/60 border border-transparent'}`} data-id="element-384">
                <div className={`flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center transition-colors duration-200 ${activeIndex === index ? `${niche.bgColor} ${niche.color}` : 'bg-navy-100/60 text-navy-400 group-hover:bg-navy-100 group-hover:text-navy-500'}`} data-id="element-385">
                  {niche.icon}
                </div>
                <div className="flex-1 min-w-0" data-id="element-386">
                  <div className={`text-sm font-semibold truncate transition-colors ${activeIndex === index ? 'text-navy-900' : 'text-navy-600 group-hover:text-navy-800'}`} data-id="element-387">
                    {niche.title}
                  </div>
                  <div className="text-xs text-navy-400" data-id="element-388">
                    {niche.positions.length} {t.niches.positions_label}
                  </div>
                </div>
                {activeIndex === index && <motion.div layoutId="niche-indicator" className={`w-1.5 h-8 rounded-full ${niche.bgColor}`} transition={{
              type: 'spring',
              stiffness: 400,
              damping: 30
            }} data-id="element-389" />}
              </button>)}
          </div>

          <div className="col-span-8" data-id="element-390">
            <AnimatePresence mode="wait" data-id="element-391">
              <motion.div key={activeIndex} initial={{
              opacity: 0,
              x: 12
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -12
            }} transition={{
              duration: 0.25
            }} className="bg-white rounded-2xl border border-navy-100 shadow-sm p-8 min-h-[420px]" data-id="element-392">
                <div className="flex items-center gap-4 mb-8 pb-6 border-b border-navy-100" data-id="element-393">
                  <div className={`w-14 h-14 rounded-2xl ${active.bgColor} ${active.color} flex items-center justify-center`} data-id="element-394">
                    <div className="scale-125" data-id="element-395">{active.icon}</div>
                  </div>
                  <div data-id="element-396">
                    <h3 className="text-2xl font-extrabold text-navy-900" data-id="element-397">
                      {active.title}
                    </h3>
                    <p className="text-sm text-navy-400 mt-0.5" data-id="element-398">
                      {active.positions.length} {t.niches.positions_label}
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3" data-id="element-399">
                  {active.positions.map((pos, i) => <motion.div key={pos} initial={{
                  opacity: 0,
                  y: 6
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  duration: 0.2,
                  delay: i * 0.03
                }} className="flex items-center gap-3 py-3 px-4 rounded-xl bg-navy-50/40 border border-navy-100/50" data-id="element-400">
                      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${active.bgColor}`} data-id="element-401" />
                      <span className="text-sm font-medium text-navy-700" data-id="element-402">
                        {pos}
                      </span>
                    </motion.div>)}
                  <motion.div initial={{
                  opacity: 0,
                  y: 6
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  duration: 0.2,
                  delay: active.positions.length * 0.03
                }} className="flex items-center gap-3 py-3 px-4 rounded-xl border border-dashed border-navy-200 col-span-2" data-id="element-403">
                    <span className="w-2 h-2 rounded-full flex-shrink-0 bg-navy-200" data-id="element-404" />
                    <span className="text-sm text-navy-400 italic" data-id="element-405">
                      {t.niches.other_positions}
                    </span>
                  </motion.div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 16
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5,
        delay: 0.3
      }} className="mt-10 text-center" data-id="element-406">
          <a href="#contact" className="inline-flex items-center justify-center gap-2 px-7 py-4 text-base font-semibold text-white bg-primary-600 rounded-xl hover:bg-primary-700 transition-all shadow-lg shadow-primary-600/30 hover:shadow-xl hover:shadow-primary-600/35 hover:-translate-y-0.5" data-id="element-407">
            {'\u0417\u0430\u043C\u043E\u0432\u0438\u0442\u0438 \u043F\u0456\u0434\u0431\u0456\u0440 \u0441\u043F\u0435\u0446\u0456\u0430\u043B\u0456\u0441\u0442\u0430'}
          </a>
        </motion.div>
      </div>
    </section>;
}